import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import WarningIcon from "@mui/icons-material/Warning";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useApiFetch } from "../../Utils/apiFetch";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { useLang } from "../../context/LanguageContext";
import { getExpiringStockItems } from "../../Utils/expiringStock";

const KpiCard = ({ icon, label, value, color, route, loading, error }) => {
    const navigate = useNavigate();
    return (
        <Paper
            elevation={0}
            onClick={() => !loading && !error && navigate(route)}
            sx={{
                p: 3,
                borderRadius: 3,
                cursor: loading || error ? "default" : "pointer",
                background: `linear-gradient(135deg, ${color[0]} 0%, ${color[1]} 100%)`,
                color: "white",
                minHeight: 120,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                "&:hover": loading || error ? {} : { opacity: 0.92 },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {icon}
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                    {label}
                </Typography>
            </Box>
            {loading ? (
                <CircularProgress size={28} sx={{ color: "white", alignSelf: "flex-end" }} />
            ) : error ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, alignSelf: "flex-end" }}>
                    <ErrorOutlineIcon fontSize="small" />
                    <Typography variant="caption">Error</Typography>
                </Box>
            ) : (
                <Typography variant="h4" fontWeight={700} sx={{ alignSelf: "flex-end" }}>
                    {value}
                </Typography>
            )}
        </Paper>
    );
};

const Dashboard = () => {
    const apiFetch = useApiFetch();
    const authHeader = useAuthHeader();
    const { t } = useLang();

    const [salesState, setSalesState] = useState({ loading: true, error: false, value: "" });
    const [unpaidState, setUnpaidState] = useState({ loading: true, error: false, value: "" });
    const [lowStockState, setLowStockState] = useState({ loading: true, error: false, value: "" });
    const [expiringState, setExpiringState] = useState({ loading: true, error: false, value: "" });

    useEffect(() => {
        const fetchSales = async () => {
            try {
                const response = await apiFetch(`${REACT_APP_BACKEND_URL}/invoices/daily-report`, {
                    headers: { Authorization: authHeader },
                });
                if (!response.ok) throw new Error();
                const body = await response.json();
                setSalesState({ loading: false, error: false, value: `$${body.data.summary.salesIncome}` });
            } catch {
                setSalesState({ loading: false, error: true, value: "" });
            }
        };

        const fetchUnpaid = async () => {
            try {
                const response = await apiFetch(`${REACT_APP_BACKEND_URL}/invoices/unpaid`, {
                    headers: { Authorization: authHeader },
                });
                if (!response.ok) throw new Error();
                const body = await response.json();
                const total = body.data.reduce((sum, inv) => sum + Number(inv.remainingAmount), 0);
                setUnpaidState({ loading: false, error: false, value: `$${total.toFixed(2)}` });
            } catch {
                setUnpaidState({ loading: false, error: true, value: "" });
            }
        };

        const fetchLowStock = async () => {
            try {
                const response = await apiFetch(`${REACT_APP_BACKEND_URL}/items/below-min-quantity`, {
                    headers: { Authorization: authHeader },
                });
                if (!response.ok) throw new Error();
                const body = await response.json();
                setLowStockState({ loading: false, error: false, value: String(body.items.length) });
            } catch {
                setLowStockState({ loading: false, error: true, value: "" });
            }
        };

        const fetchExpiring = async () => {
            try {
                const response = await apiFetch(`${REACT_APP_BACKEND_URL}/items`, {
                    headers: { Authorization: authHeader },
                });
                if (!response.ok) throw new Error();
                const body = await response.json();
                const expiring = getExpiringStockItems(body.data);
                setExpiringState({ loading: false, error: false, value: String(expiring.length) });
            } catch {
                setExpiringState({ loading: false, error: true, value: "" });
            }
        };

        fetchSales();
        fetchUnpaid();
        fetchLowStock();
        fetchExpiring();
        // Intentionally empty deps: these 4 fetches run once on mount only —
        // apiFetch's identity changing, or authHeader recomputing on every
        // render (react-auth-kit doesn't memoize it), shouldn't re-trigger them.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ width: "100%" }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
                {t("DASHBOARD")}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        icon={<PointOfSaleIcon />}
                        label={t("TODAYS_SALES")}
                        route="/daily-report"
                        color={["#43a047", "#1b5e20"]}
                        {...salesState}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        icon={<WarningIcon />}
                        label={t("UNPAID_INVOICES")}
                        route="/unpaid-invoices"
                        color={["#fb8c00", "#e65100"]}
                        {...unpaidState}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        icon={<ProductionQuantityLimitsIcon />}
                        label={t("ITEMS_BELOW_MIN")}
                        route="/items-below-min-quantity"
                        color={["#e53935", "#b71c1c"]}
                        {...lowStockState}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        icon={<HourglassTopIcon />}
                        label={t("EXPIRING_SOON")}
                        route="/items-expiring-soon"
                        color={["#00897b", "#004d40"]}
                        {...expiringState}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;
